/**
 * usePreviewUpgrade — S10-15
 *
 * Mutation hook for POST /api/v2/subscriptions/preview_upgrade
 *
 * FIN-SWEEP-FE-3 (F11): Routed via lib/api-client.ts so the Demo-Token-aware
 * Authorization header is set automatically (lib/api-client.ts:75-102). Method,
 * path, and body shape are unchanged. 422 / other-ok behavior preserved.
 */
import { useState, useCallback } from 'react'
import apiClient from '../../lib/api-client'
import i18n from '../../i18n'

// /api/v2/subscriptions/preview_upgrade is not in @heliumtracker/api-types yet,
// hence the `as any` cast (same pattern as usePayments.ts:42-45).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const client = apiClient as any

export type PreviewUpgradeResult =
  | {
      ok: true
      current_plan: string
      target_plan: string
      currency: string
      proration_credit: number
      total: number
      // Issue BE#24: welcher Pfad greift bei Bestaetigung. 'conversion' = Premium
      // ohne Stripe-Abo, Restlaufzeit wird pro rata umgerechnet (keine Zahlung),
      // neues Ablaufdatum in conversion_expires_at.
      mode: 'stripe_swap' | 'conversion' | 'checkout'
      conversion_expires_at: string | null
      // Premium-Checkout-Redesign: zusätzliche Restwert-/Umrechnungsfelder,
      // optional bis das BE sie durchgaengig liefert.
      unused_credit?: number
      days_remaining?: number
      new_expires_at?: string
      converted_days?: number
      direction?: 'upgrade' | 'downgrade'
      target_total?: number
    }
  | { ok: false; error: string }

interface ApiErrorBody {
  status?: number
  body?: { message?: string; error?: string } | string
}

async function previewUpgradeRequest(
  targetPlanId: string,
  durationMonths?: number,
): Promise<PreviewUpgradeResult> {
  try {
    const body =
      durationMonths !== undefined
        ? { target_plan_id: targetPlanId, duration_months: durationMonths }
        : { target_plan_id: targetPlanId }
    const result = await client.POST('/api/v2/subscriptions/preview_upgrade', { body })

    if (result.error) {
      return { ok: false, error: i18n.t('premium:upgradeModal.previewError') }
    }

    // Das Backend liefert seit 04.09.2026 kein `tax` mehr (Betreiber ist eine
    // US-LLC, keine Umsatzsteuer) — dafuer `currency`. Ein Feld, das hier
    // erwartet, aber nicht gesendet wird, endet im Modal als
    // `undefined.toFixed()` und reisst die ganze App in den Error-Boundary.
    const json = result.data as {
      current_plan: string
      target_plan: string
      currency?: string
      mode?: 'stripe_swap' | 'conversion' | 'checkout'
      conversion_expires_at?: string | null
      proration_credit: number
      total: number
      unused_credit?: number
      days_remaining?: number
      new_expires_at?: string
      converted_days?: number
      direction?: 'upgrade' | 'downgrade'
      target_total?: number
    }

    return {
      ok: true,
      current_plan: json.current_plan,
      target_plan: json.target_plan,
      currency: json.currency ?? 'usd',
      mode: json.mode ?? 'checkout',
      conversion_expires_at: json.conversion_expires_at ?? null,
      proration_credit: json.proration_credit,
      total: json.total,
      unused_credit: json.unused_credit,
      days_remaining: json.days_remaining,
      new_expires_at: json.new_expires_at,
      converted_days: json.converted_days,
      direction: json.direction,
      target_total: json.target_total,
    }
  } catch (e) {
    const err = e as ApiErrorBody
    if (err?.status === 422) {
      return { ok: false, error: i18n.t('premium:upgradeModal.invalidPlan') }
    }
    return { ok: false, error: i18n.t('premium:upgradeModal.previewError') }
  }
}

export function usePreviewUpgrade() {
  const [isPending, setIsPending] = useState(false)

  const previewUpgrade = useCallback(
    async (targetPlanId: string, durationMonths?: number): Promise<PreviewUpgradeResult> => {
      setIsPending(true)
      try {
        return await previewUpgradeRequest(targetPlanId, durationMonths)
      } finally {
        setIsPending(false)
      }
    },
    [],
  )

  return { previewUpgrade, isPending }
}
